import type { LocalState, DailyRecord } from '@/lib/local-state';
import { SHOTS_PER_DAY } from '@/lib/constants';
import { computeBadges, type BadgeId as PerformanceBadgeId } from '@/lib/badges';
import { unlockedEngagementBadges, type EngagementBadgeId } from '@/lib/engagement-badges';

/**
 * Strict completion check used ONLY by personal-stats aggregation.
 * The shared isComplete() in local-state.ts returns true for any v1 record
 * regardless of shot count — needed for scene routing of legacy mid-round
 * sessions. For stats we want true historical completion, so v1 records must
 * have hit the v1 cap (5 shots) and v2 records must have hit SHOTS_PER_DAY.
 *
 * Do not move this into local-state.ts — the looser isComplete() is correct
 * for its callers (Plan #6 §10.1 catch).
 */
export function isCompleteStrict(record: DailyRecord): boolean {
  if (record.schemaVersion === 1) return record.shots.length === 5;
  return record.shots.length === SHOTS_PER_DAY;
}

export interface PersonalStatsView {
  daysPlayed: number;
  currentStreak: number;
  longestStreak: number;
  /** scoreDistribution[i] = count of completed days with score === i (i in 0..SHOTS_PER_DAY). */
  scoreDistribution: number[];
  earnedEngagementBadges: EngagementBadgeId[];
  earnedPerformanceBadges: PerformanceBadgeId[];
}

export function computePersonalStats(state: LocalState): PersonalStatsView {
  const completed = Object.values(state.history).filter(isCompleteStrict);
  const dist = new Array(SHOTS_PER_DAY + 1).fill(0);
  const perfSet = new Set<PerformanceBadgeId>();
  for (const r of completed) {
    if (r.score >= 0 && r.score <= SHOTS_PER_DAY) dist[r.score]++;
    for (const id of computeBadges(r.shots)) perfSet.add(id);
  }
  return {
    daysPlayed: completed.length,
    currentStreak: state.streak.current,
    longestStreak: state.streak.longest,
    scoreDistribution: dist,
    earnedEngagementBadges: unlockedEngagementBadges(state.streak.longest),
    earnedPerformanceBadges: Array.from(perfSet),
  };
}
